import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SercomButton from './SercomButton';

const VERSION_URL = '/version.json';
const CHECK_INTERVAL_MS = 60 * 1000;

type VersionResponse = {
  version?: string;
};

const fetchVersion = async (): Promise<string | null> => {
  const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
    cache: 'no-store',
  });

  if (!response.ok) {
    return null;
  }

  const data = (await response.json()) as VersionResponse;
  return data.version ?? null;
};

const AppUpdateBanner = () => {
  const { t } = useTranslation();
  const currentVersion = useRef<string | null>(null);
  const [hasUpdate, setHasUpdate] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return;
    }

    let isActive = true;

    const checkForUpdate = async () => {
      try {
        const latestVersion = await fetchVersion();

        if (!isActive || !latestVersion) {
          return;
        }

        if (!currentVersion.current) {
          currentVersion.current = latestVersion;
          return;
        }

        if (latestVersion !== currentVersion.current) {
          setHasUpdate(true);
        }
      } catch {
        // Ignore transient network errors. The next poll will try again.
      }
    };

    checkForUpdate();
    const intervalId = window.setInterval(checkForUpdate, CHECK_INTERVAL_MS);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, []);

  if (!hasUpdate) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.tooltip + 1,
      }}
    >
      <Alert
        severity="info"
        action={<SercomButton text={t('Refresh')} variant="outlined" onClick={() => window.location.reload()} />}
        sx={{
          'borderRadius': 0,
          'justifyContent': 'center',
          '& .MuiAlert-message': {
            flexGrow: 0,
          },
        }}
      >
        {t('NewVersionAvailable')}
      </Alert>
    </Box>
  );
};

export default AppUpdateBanner;
